import { Button, Field, FieldCheckbox } from '@ibe/ui';
import { Columns3 } from 'lucide-react';
import { useId, useState } from 'react';

import { useAppStore } from '../../lib/state/store.js';
import {
  type ColumnDescriptor,
  resolveVisibleColumns,
  toggleColumn,
} from '../../lib/resource-list/columns.js';

/**
 * Which columns a reader wants to see.
 *
 * A plain disclosure rather than a Radix popover: the content is a short list
 * of checkboxes, and a native `<details>`-style toggle needs no focus
 * trapping to be usable with a keyboard. `@ibe/ui` would be the right home
 * for this if a second application needed it; today only the portal does, so
 * it stays in ring 2.
 */

export interface ColumnPickerProps<TRow> {
  /** Namespace for the stored selection. Matches the list's cache root. */
  readonly resource: string;
  readonly columns: readonly ColumnDescriptor<TRow>[];
}

export function ColumnPicker<TRow>({
  resource,
  columns,
}: ColumnPickerProps<TRow>): React.JSX.Element {
  const [open, setOpen] = useState(false);
  const panelId = useId();

  // Selector-scoped: this component re-renders when *its* resource's
  // selection changes, not when any other preference does.
  const selection = useAppStore((state) => state.prefs.columns[resource]);
  const setColumnSelection = useAppStore((state) => state.setColumnSelection);
  const clearColumnSelection = useAppStore((state) => state.clearColumnSelection);

  const visible = new Set(resolveVisibleColumns(columns, selection).map((column) => column.id));

  return (
    <div className="relative">
      <Button
        type="button"
        variant="secondary"
        size="sm"
        aria-expanded={open}
        aria-controls={panelId}
        onClick={() => {
          setOpen((previous) => !previous);
        }}
      >
        <Columns3 className="h-4 w-4" aria-hidden="true" />
        Columns
      </Button>

      {!open ? null : (
        <div
          id={panelId}
          className="absolute right-0 z-dropdown mt-1 w-56 rounded-md border bg-background p-2 shadow-overlay"
        >
          <fieldset className="space-y-1">
            <legend className="px-1 pb-1 text-caption text-muted-foreground">
              Visible columns
            </legend>

            {columns.map((column) => (
              <Field key={column.id}>
                <FieldCheckbox
                  checked={visible.has(column.id)}
                  // Checked and disabled rather than absent, so the reader can
                  // see why this one cannot be turned off.
                  disabled={column.alwaysVisible === true}
                  onCheckedChange={() => {
                    setColumnSelection(resource, toggleColumn(columns, selection, column.id));
                  }}
                  className="rounded px-1 py-1 hover:bg-accent"
                >
                  {column.header}
                </FieldCheckbox>
              </Field>
            ))}
          </fieldset>

          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="mt-1 w-full"
            onClick={() => {
              clearColumnSelection(resource);
            }}
          >
            Reset to defaults
          </Button>
        </div>
      )}
    </div>
  );
}
